import { routes } from '@/domains/core/routing/routes/routes.default';
import { createURL } from '@/domains/core/routing/URLParser';

import type {
  QueryCategory,
  StaticSuggestionType,
  SuggestionType,
} from '../interfaces/suggestions';
import { cleanEmptyHtmlTags, cleanHtmlTags } from './cleanHtmlTags';
import { nameToSlug } from './nameToSlug';

const highlightTerm = (term: string, value: string, type: string) => {
  const regex = new RegExp(term, 'i');

  if (type === 'query') {
    const formatedSuggestion = `<b>${value.replace(
      regex,
      `</b>${term}<b>`,
    )}</b>`;

    return cleanEmptyHtmlTags(formatedSuggestion);
  }

  return value.replace(regex, `<strong>${term}</strong>`);
};

const formatQueryCategory = (
  name: string,
  queryCategory: QueryCategory,
): QueryCategory | undefined => {
  if (!queryCategory.name || !queryCategory.id) return undefined;

  const { name: categoryName, id: categoryId } = queryCategory;

  return {
    name: categoryName,
    id: categoryId,
    slug: createURL(
      routes.search,
      { query: nameToSlug(name, '+') },
      {
        category_names: categoryName,
        catId: categoryId,
      },
    ),
  };
};

export const formatSuggestion = ({
  name,
  termQuery,
  type,
  objectID,
  highlight,
  queryCategory,
}: {
  name: string;
  termQuery: string;
  type: SuggestionType | Extract<StaticSuggestionType, 'product'>;
  objectID?: string;
  highlight?: boolean;
  queryCategory?: QueryCategory;
}) => {
  const sanitizedTermQuery = cleanHtmlTags(termQuery);
  const sanitizedName = cleanHtmlTags(name);
  const term = highlight
    ? highlightTerm(sanitizedTermQuery, sanitizedName, type)
    : sanitizedName;

  switch (type) {
    case 'brand':
      return {
        name: term,
        type,
        objectID,
        slug: createURL(routes.brand, {
          brand: nameToSlug(sanitizedName),
          ...(objectID && { id: objectID }),
        }),
      };
    case 'category':
      return {
        name: term,
        type,
        objectID,
        slug: createURL(routes.category, {
          category: nameToSlug(sanitizedName),
          ...(objectID && { id: objectID }),
        }),
      };
    case 'history':
    case 'query':
      return {
        name: term,
        type,
        objectID,
        slug: createURL(routes.search, {
          query: nameToSlug(sanitizedName, '+'),
        }),
        ...(queryCategory && {
          queryCategory: formatQueryCategory(sanitizedName, queryCategory),
        }),
      };
    default:
      return {
        name: term,
        objectID,
        type,
      };
  }
};
